// Renders a single asset in AssetList

import DeleteAssetMenuItem from 'components/assets/list/delete-menu-item'
import DuplicateAssetMenuItem from 'components/assets/list/duplicate-menu-item'
import { LockIcon } from 'components/icons'
import LogoMark from 'components/layout/logo-mark'
import { Attribution } from 'components/ui'
import { Menu, Tooltip } from 'components/ui'
import { useCurrentUser } from 'context/current-user-context'
import classNames from 'helpers/class-names'
import getInitials from 'helpers/get-initials'
import { useHash } from 'hooks/use-hash'
import { AssetData } from 'lib/assets/asset'
import { useRouter } from 'next/router'
import { FC, useState } from 'react'
import { useMemo } from 'react'

interface Props {
  asset: AssetData
  selected?: boolean
  isCardStyle?: boolean // Whether to use card style for fixed width asset list or fullscreen mode
  onDestroy: () => void
  onDuplicate: () => void
}

const ListItem: FC<Props> = ({
  asset,
  onDestroy,
  onDuplicate,
  selected,
  isCardStyle = true,
}) => {
  const typeLabel = 'Asset'
  const [visible, setVisible] = useState<boolean>(true)
  const user = useCurrentUser()
  const { hash } = useHash()

  const readOnly = asset.teamId != user.teamId
  const isGeneric = false

  const owner = user.getTeamMember(asset.ownerId)

  const handleDelete = () => {
    setVisible(false)
    setTimeout(() => onDestroy(), 500)
  }

  const isDefaultAsset = !owner?.id

  // Content for initials badge
  const initials = useMemo(() => getInitials(asset.name), [asset.name])

  // Select this item
  const router = useRouter()
  const selectItem = () => {
    if (selected) {
      router.push(`/${asset.type}/${asset.uuid}${hash}`)
    } else {
      router.push(`/${asset.type}/${asset.uuid}${hash}`)
    }
  }

  return (
    <li
      onClick={selectItem}
      className={classNames(
        'flex items-center border-b border-gray-100 bg-white',
        {
          'rounded-md border': isCardStyle,
        },
      )}>
      <div
        className={classNames(
          'flex w-full cursor-pointer items-center gap-3 p-4 tracking-normal',
          {
            'border-l-8 border-l-info pl-2': selected,
            'hover:border-l-8 hover:border-gray-50 hover:pl-2': !selected,
            'rounded-l-md': isCardStyle,
          },
        )}>
        {!isDefaultAsset ? (
          <div className="flex aspect-square h-10 items-center justify-center rounded-lg bg-gray-200 text-lg font-semibold text-white">
            {initials}
          </div>
        ) : (
          <LogoMark className="size-10 text-gray-400" />
        )}
        <div className="flex flex-col text-left">
          <div className="flex items-center gap-1 font-bold text-gray-500">
            {asset.name}{' '}
            {readOnly && (
              <span className="text-gray-200 hover:text-gray-300">
                <Tooltip tip="Read-only">
                  <LockIcon className="size-4" />
                </Tooltip>
              </span>
            )}
          </div>
          <div className="text-xs font-semibold text-gray-300">
            {!isDefaultAsset ? (
              // Owned by user's team
              <Attribution
                className="truncate break-words text-xs leading-4 md:line-clamp-2 md:whitespace-normal"
                by={owner?.fullName}
                at={asset.validFrom}
                atClassName="font-bold"
                byClassName="font-bold"
                action="Modified"
              />
            ) : (
              // Default/read-only asset by Toolpath
              <div className="text-xs leading-4">
                From <span className="font-bold">Team Toolpath</span>
              </div>
            )}
          </div>
        </div>
      </div>
      <Menu className="flex px-2">
        <Menu.Item
          href={`/${asset.type}/${asset.uuid}${hash}`}
          label={user.readOnly || readOnly ? 'View' : 'Edit'}
        />
        {!user.readOnly && (
          <>
            <DuplicateAssetMenuItem
              typeLabel={typeLabel}
              asset={asset}
              onSuccess={onDuplicate}
            />
            {!readOnly && !isGeneric && (
              <>
                <Menu.Divider />
                <DeleteAssetMenuItem
                  typeLabel={typeLabel}
                  asset={asset}
                  onSuccess={handleDelete}
                />
              </>
            )}
          </>
        )}
      </Menu>
    </li>
  )
}

export default ListItem
